// User preferences — display-layer only, localStorage-backed for now.
// Server-side persistence (so preferences travel between devices) is a
// future-Phase concern; for the MVP a single-device cookie-shaped store
// is enough.
//
// Why a context provider? So a single localStorage read happens at mount
// and every consumer subscribes to the same state — toggling the unit
// updates every temperature display in one render pass.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { ECUnit, LevelUnit, TemperatureUnit } from "./units";

const STORAGE_KEY = "joesgrove.preferences";

export interface Preferences {
  temperatureUnit: TemperatureUnit;
  ecUnit: ECUnit;
  levelUnit: LevelUnit;
}

const DEFAULTS: Preferences = {
  temperatureUnit: "C",
  ecUnit: "mS/cm",
  levelUnit: "gal",
};

interface Ctx extends Preferences {
  setTemperatureUnit(u: TemperatureUnit): void;
  setEcUnit(u: ECUnit): void;
  setLevelUnit(u: LevelUnit): void;
}

const PrefsCtx = createContext<Ctx | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Preferences>(() => {
    if (typeof window === "undefined") return DEFAULTS;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULTS;
      const parsed = JSON.parse(raw) as Partial<Preferences>;
      return {
        temperatureUnit:
          parsed.temperatureUnit === "F" || parsed.temperatureUnit === "C"
            ? parsed.temperatureUnit
            : DEFAULTS.temperatureUnit,
        ecUnit:
          parsed.ecUnit === "mS/cm" || parsed.ecUnit === "ppm700"
            ? parsed.ecUnit
            : DEFAULTS.ecUnit,
        levelUnit:
          parsed.levelUnit === "gal" ||
          parsed.levelUnit === "in" ||
          parsed.levelUnit === "cm"
            ? parsed.levelUnit
            : DEFAULTS.levelUnit,
      };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // localStorage may be blocked (private mode); preferences just don't persist.
    }
  }, [prefs]);

  const setTemperatureUnit = useCallback((u: TemperatureUnit) => {
    setPrefs((p) => ({ ...p, temperatureUnit: u }));
  }, []);
  const setEcUnit = useCallback((u: ECUnit) => {
    setPrefs((p) => ({ ...p, ecUnit: u }));
  }, []);
  const setLevelUnit = useCallback((u: LevelUnit) => {
    setPrefs((p) => ({ ...p, levelUnit: u }));
  }, []);

  const value = useMemo<Ctx>(
    () => ({ ...prefs, setTemperatureUnit, setEcUnit, setLevelUnit }),
    [prefs, setTemperatureUnit, setEcUnit, setLevelUnit],
  );

  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}

export function usePreferences(): Ctx {
  const v = useContext(PrefsCtx);
  if (!v) {
    throw new Error("usePreferences must be called inside <PreferencesProvider>");
  }
  return v;
}
